import React, { useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { getCurrentUser } from 'aws-amplify/auth';
import { notifyLogin, fetchUserInfo } from './services/authWebhook';
import Header from './components/Header';
import Home from './pages/Home';
import Blog from './pages/Blog';
import WorkflowDetail from './pages/WorkflowDetail';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import Footer from './components/Footer';
import Plan from './pages/Plan';
import PaymentSuccess from './pages/PaymentSuccess';
import BlogService from './pages/BlogService';
import Photo from './pages/Photo';
import Law from './pages/Law';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import About from './pages/About';
import Contact from './pages/Contact';
import Support from './pages/Support';
import SupportLegal from './pages/SupportLegal';
import './App.css';

function AppContent() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planEndedAt, setPlanEndedAt] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [redirectHandled, setRedirectHandled] = useState(false);
  const [toast, setToast] = useState(null);
  const isPhotoPage = location.pathname === '/photo';
  const isLawPage = location.pathname === '/law';
  const isOAuthCallback = (() => {
    try {
      const params = new URLSearchParams(location.search);
      // Cognito Hosted UI callback (responseType=code)
      return params.has('code') || params.has('error') || params.has('state');
    } catch {
      return false;
    }
  })();

  const oauthError = (() => {
    try {
      const params = new URLSearchParams(location.search);
      const error = params.get('error');
      const errorDescription = params.get('error_description');
      return error
        ? {
            error,
            errorDescription,
          }
        : null;
    } catch {
      return null;
    }
  })();

  // OAuth 콜백에서 에러가 난 경우: 토스트 표시 + 로그인 시작 위치로 복귀
  useEffect(() => {
    if (!isOAuthCallback) return;
    if (!oauthError?.error) return;

    // Cognito는 error_description에 공백을 + 로 인코딩해서 주는 경우가 있어 보정
    const desc = oauthError.errorDescription
      ? decodeURIComponent(String(oauthError.errorDescription || '').replace(/\+/g, ' '))
      : null;

    const normalizedDesc = (desc || '').toLowerCase();
    const isMissingEmail =
      normalizedDesc.includes('attributes required') && normalizedDesc.includes('[email]');

    const message = isMissingEmail
      ? '카카오 로그인 실패: 이메일을 가져올 수 없습니다. (카카오계정에 이메일이 없거나, Cognito-Kakao(OIDC) email 스코프/매핑 설정을 확인해주세요)'
      : desc
        ? `로그인 실패: ${desc}`
        : `로그인 실패: ${oauthError.error}`;

    try {
      localStorage.setItem('app_toast', JSON.stringify({ type: 'error', message, ts: Date.now() }));
      window.dispatchEvent(new Event('app_toast'));
    } catch (e) {
      // ignore
    }

    let redirect = null;
    try {
      redirect = localStorage.getItem('login_redirect');
      if (redirect) localStorage.removeItem('login_redirect');
    } catch (e) {
      redirect = null;
    }

    // 시작 위치가 있으면 복귀 (/photo 등). 없으면 홈 유지.
    if (redirect && redirect.startsWith('/') && window.location.pathname !== redirect) {
      window.location.replace(redirect);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOAuthCallback, location.search]);

  // OAuth 콜백 중에만 login_redirect를 근거로 화면을 잠깐 가림 (플래시 방지)
  // 일반 진입에서 login_redirect가 남아있더라도 화면을 가리면 "흰 화면 잠김"이 발생할 수 있어 제한한다.
  const pendingLoginRedirect = (() => {
    if (!isOAuthCallback) return null;
    try {
      const r = localStorage.getItem('login_redirect');
      return r && r !== location.pathname ? r : null;
    } catch {
      return null;
    }
  })();

  // 로그인 유저 감지 및 profile fetch
  useEffect(() => {
    const fetchUserAndPlan = async () => {
      try {
        // Hosted UI OAuth callback (code/state)에서는 먼저 세션을 요청해서
        // Amplify가 authorization code -> token 교환을 완료하도록 유도한다.
        if (isOAuthCallback) {
          try {
            const { fetchAuthSession } = await import('aws-amplify/auth');
            await fetchAuthSession();
          } catch (e) {
            // code/state는 있는데 token 교환이 실패하는 경우가 있어 사용자에게 표시
            const message =
              e?.message
                ? `로그인 처리 실패: ${e.message}`
                : '로그인 처리 실패: 인증 토큰 교환에 실패했습니다.';
            try {
              localStorage.setItem('app_toast', JSON.stringify({ type: 'error', message, ts: Date.now() }));
              window.dispatchEvent(new Event('app_toast'));
            } catch (e2) {
              // ignore
            }
            // 실패했는데도 URL에 code/state가 남아 있으면 혼란스러우므로 쿼리만 정리
            try {
              window.history.replaceState({}, document.title, window.location.pathname);
            } catch (e3) {
              // ignore
            }
            throw e;
          }
        }

        const currentUser = await getCurrentUser();
        setUser(currentUser);
        // 세션/토큰 얻기
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (idToken) {
          // JWT claims에서 사용자 정보 추출
          let claims = {};
          try {
            const parts = idToken.split('.');
            if (parts.length >= 2) {
              const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
              const padded = b64 + '==='.slice((b64.length + 3) % 4);
              claims = JSON.parse(atob(padded));
            }
          } catch {}
          const sub = claims?.sub || currentUser?.userId || '';
          const email = claims?.email || '';
          const name = claims?.name || claims?.nickname || '';

          // n8n 웹훅: signin/signup 알림 + user_info 조회
          notifyLogin({ sub, email, name, project: 'homepage' });
          fetchUserInfo({ sub, email, name, project: 'homepage' }).catch(() => {});

          const response = await fetch('https://1cw148rxb8.execute-api.ap-northeast-2.amazonaws.com/api/get-profile', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${idToken}`,
            },
          });
          const rawBody = await response.text();
          let data;
          try {
            data = JSON.parse(rawBody);
          } catch {
            data = {};
          }
          setCurrentPlan(data.current_plan || null);
          setPlanEndedAt(data.plan_ended_at || null);
        } else {
          setCurrentPlan(null);
          setPlanEndedAt(null);
        }
      } catch (err) {
        setUser(null);
        setCurrentPlan(null);
        setPlanEndedAt(null);
      } finally {
        setAuthChecked(true);
      }
    };
    fetchUserAndPlan();
  }, [isOAuthCallback]);

  // 전역 토스트: 리다이렉트/결제 등 다른 페이지에서 설정한 메시지를 한번만 표시
  useEffect(() => {
    const consumeToast = () => {
      let raw = null;
      try {
        raw = localStorage.getItem('app_toast');
        if (raw) localStorage.removeItem('app_toast');
      } catch (e) {
        raw = null;
      }
      if (!raw) return;

      try {
        const parsed = JSON.parse(raw);
        if (!parsed?.message) return;
        setToast({ type: parsed.type || 'info', message: parsed.message });
        const t = setTimeout(() => setToast(null), 2600);
        return () => clearTimeout(t);
      } catch (e) {
        // ignore
      }
    };

    // 라우트 변경 시 한번 소비
    const cleanup = consumeToast();

    // 같은 페이지 내에서도 토스트를 띄울 수 있도록 커스텀 이벤트 지원
    const onToastEvent = () => consumeToast();
    window.addEventListener('app_toast', onToastEvent);

    return () => {
      window.removeEventListener('app_toast', onToastEvent);
      if (typeof cleanup === 'function') cleanup();
    };
  }, [location.pathname]);

  // 로그인 완료 후: 로그인 시작 위치(login_redirect)로 복귀
  // - 값이 남아있으면 항상 소비/삭제해서 "예전 /photo 값이 남아 강제 이동" 같은 문제를 방지
  useLayoutEffect(() => {
    if (!authChecked) return;

    // OAuth 에러 콜백은 위 useEffect에서 별도 처리 (토스트 + 복귀)하므로 여기서는 종료
    if (isOAuthCallback && oauthError?.error) {
      setRedirectHandled(true);
      return;
    }

    let redirect = null;
    try {
      redirect = localStorage.getItem('login_redirect');
    } catch (e) {
      redirect = null;
    }

    // OAuth 콜백이 아닌 "일반 진입"인데 login_redirect가 남아있으면 stale로 간주하고 즉시 정리
    // (이 값 때문에 화면이 흰색으로 잠기거나, 예전 값으로 강제 이동되는 문제 방지)
    if (redirect && !isOAuthCallback) {
      try {
        localStorage.removeItem('login_redirect');
      } catch (e) {
        // ignore
      }
      setRedirectHandled(true);
      return;
    }

    // OAuth 콜백인데 user를 얻지 못한 경우(취소/실패 등)에도 login_redirect가 남아있으면 화면이 잠길 수 있어 정리
    if (redirect && isOAuthCallback && !user) {
      try {
        localStorage.removeItem('login_redirect');
      } catch (e) {
        // ignore
      }
      setRedirectHandled(true);
      return;
    }

    // 로그인 상태에서만 redirect 적용 (콜백 흐름에서만 사용)
    if (user && redirect && window.location.pathname !== redirect) {
      try {
        localStorage.removeItem('login_redirect');
      } catch (e) {
        // ignore
      }
      window.location.replace(redirect);
      return;
    }

    // 이미 도착한 경우 정리
    if (redirect && window.location.pathname === redirect) {
      try {
        localStorage.removeItem('login_redirect');
      } catch (e) {
        // ignore
      }
    }

    // 콜백 처리(또는 일반 진입) 완료 표시
    setRedirectHandled(true);
  }, [user, authChecked, location.pathname, isOAuthCallback, oauthError?.error]);

  // OAuth 콜백 처리 중에는 라우트 화면을 잠깐 가려서
  // "잠시 /photo가 보였다가 다시 돌아오는" 플래시를 방지
  if (pendingLoginRedirect || (isOAuthCallback && (!authChecked || !redirectHandled))) {
    return (
      <div className="App" style={{ minHeight: '100vh', background: '#fff' }} />
    );
  }

  return (
    <div className="App">
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 18,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 20000,
            padding: '12px 16px',
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600,
            color: '#fff',
            background:
              toast.type === 'success'
                ? 'rgba(34, 197, 94, 0.92)'
                : toast.type === 'error'
                  ? 'rgba(239, 68, 68, 0.92)'
                  : 'rgba(20, 20, 20, 0.92)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            backdropFilter: 'blur(10px)',
            maxWidth: 'calc(100vw - 32px)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {toast.message}
        </div>
      )}
      {!isPhotoPage && !isLawPage && <Header user={user} />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/workflow/:id" element={<WorkflowDetail />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/plan" element={<Plan user={user} currentPlan={currentPlan} planEndedAt={planEndedAt} />} />
        <Route path="/blog-service" element={<BlogService />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/photo" element={<Photo />} />
        <Route path="/law" element={<Law />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:slug" element={<ProductDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/support/:project" element={<Support />} />
        <Route path="/support/:project/:docKey" element={<SupportLegal />} />
      </Routes>
      {!isPhotoPage && !isLawPage && <Footer />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;